import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { createMcpOAuthAuthorizationCode } from "../api";
import logibudLogo from "../assets/logo/logibud_primary_darkblue.svg";
import { useUserSession } from "../context/UserContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { useUserWorkspaces } from "../hooks";

const scopeLabels: Record<string, string> = {
  "companies:read": "Companies",
  "customers:create": "Create customers",
  "customers:read": "Customers",
  "customers:update": "Update customers",
  "drivers:create": "Create drivers",
  "drivers:read": "Drivers",
  "locations:create": "Create locations",
  "locations:read": "Locations",
  "orders:create": "Create order drafts",
  "orders:read": "Orders",
  "products:read": "Products",
  "routes:read": "View driver routes",
  "routes:optimize": "Optimize driver routes (may incur usage)",
  "routes:update": "Reorder driver routes",
  "vehicles:create": "Create vehicles",
  "vehicles:read": "Vehicles",
};

const requiredParams = [
  "client_id",
  "redirect_uri",
  "response_type",
  "code_challenge",
  "code_challenge_method",
];

const OAuthAuthorizePage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { userSession, authReady } = useUserSession();
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const [isApproving, setIsApproving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const workspacesQuery = useUserWorkspaces(
    authReady ? userSession?.user.email ?? "" : "",
  );
  const workspaces = useMemo(
    () => workspacesQuery.data ?? [],
    [workspacesQuery.data],
  );

  const requestedScopes = useMemo(() => {
    const scope = searchParams.get("scope") || "drivers:read";
    return scope.split(" ").filter(Boolean);
  }, [searchParams]);
  const hasWriteAccess = requestedScopes.some(
    (scope) => scope.endsWith(":create") || scope.endsWith(":update"),
  );

  const missingParams = requiredParams.filter((param) => !searchParams.get(param));
  const invalidProtocol =
    searchParams.get("response_type") && searchParams.get("response_type") !== "code";
  const invalidPkce =
    searchParams.get("code_challenge_method") &&
    searchParams.get("code_challenge_method") !== "S256";
  const invalidRequest = missingParams.length > 0 || invalidProtocol || invalidPkce;
  const canApprove =
    authReady &&
    userSession?.token.accessToken &&
    activeWorkspace?.workspaceId &&
    !workspacesQuery.isLoading &&
    !invalidRequest &&
    !isApproving;
  const continuePath = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!workspaces.length) return;

    const selectedWorkspace =
      workspaces.find(
        (workspace) => workspace.workspaceId === activeWorkspace?.workspaceId,
      ) ?? workspaces[0];
    const activeWorkspaceIsCurrent =
      activeWorkspace?.workspaceId === selectedWorkspace.workspaceId &&
      activeWorkspace.role === selectedWorkspace.role &&
      activeWorkspace.workspaceName === selectedWorkspace.workspaceName;

    if (!activeWorkspaceIsCurrent) {
      setActiveWorkspace(selectedWorkspace);
    }
  }, [
    activeWorkspace?.role,
    activeWorkspace?.workspaceId,
    activeWorkspace?.workspaceName,
    setActiveWorkspace,
    workspaces,
  ]);

  const approve = async () => {
    if (!canApprove || !activeWorkspace || !userSession) return;

    setIsApproving(true);
    setErrorMessage(null);

    try {
      const response = await createMcpOAuthAuthorizationCode(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        {
          client_id: searchParams.get("client_id") || "",
          redirect_uri: searchParams.get("redirect_uri") || "",
          response_type: "code",
          state: searchParams.get("state"),
          code_challenge: searchParams.get("code_challenge") || "",
          code_challenge_method: "S256",
          scope: requestedScopes.join(" "),
          resource: searchParams.get("resource"),
          name: searchParams.get("client_id")
            ? `OAuth: ${searchParams.get("client_id")}`
            : "OAuth MCP client",
        },
      );

      if (!response?.redirect_uri) {
        throw new Error("Logibud did not return a redirect URL.");
      }

      window.location.assign(response.redirect_uri);
    } catch (error: any) {
      const status = error?.response?.status;
      setErrorMessage(
        status === 401 || status === 403
          ? "Only workspace owners can authorize MCP access for this workspace."
          : error?.message || "Could not authorize this MCP client.",
      );
      setIsApproving(false);
    }
  };

  const clientName = searchParams.get("client_id") || "Unknown client";
  const workspaceName =
    activeWorkspace?.workspaceName || activeWorkspace?.workspaceId || "No workspace selected";

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-8 text-gray-900 sm:py-12">
      <section className="mx-auto w-full max-w-lg overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-white px-6 py-6">
          <div className="mb-5 inline-flex">
            <img
              alt="Logibud"
              className="h-9 w-auto"
              src={logibudLogo}
            />
          </div>
          <h1 className="text-2xl font-semibold tracking-normal text-gray-900">Authorize Logibud access</h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            Connect this AI client to selected data and actions in your active Logibud workspace.
          </p>
        </div>

        <div className="space-y-5 px-6 py-6">
          {!authReady ? (
            <p className="rounded-md bg-gray-100 p-3 text-sm text-gray-700">
              Checking login status...
            </p>
          ) : !userSession ? (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
              You need to log in before authorizing an MCP client.
              <div className="mt-3">
                <Link
                  className="font-medium text-lgb-primary hover:underline"
                  to={`/login?continue=${encodeURIComponent(continuePath)}`}
                >
                  Log in to Logibud
                </Link>
              </div>
            </div>
          ) : !activeWorkspace ? (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
              Select an active workspace before authorizing this client.
            </div>
          ) : invalidRequest ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900">
              <p className="font-medium">This authorization request is invalid.</p>
              {missingParams.length > 0 && (
                <p className="mt-1">Missing: {missingParams.join(", ")}</p>
              )}
              {invalidProtocol && <p className="mt-1">response_type must be code.</p>}
              {invalidPkce && <p className="mt-1">code_challenge_method must be S256.</p>}
            </div>
          ) : null}

          <div className="rounded-lg border border-gray-200">
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="text-xs font-medium uppercase text-gray-500">Client</p>
              <p className="mt-1 break-all text-sm font-medium text-gray-900">
                {clientName}
              </p>
            </div>
            <div className="px-4 py-3">
              <label className="text-xs font-medium uppercase text-gray-500" htmlFor="oauth-workspace">
                Workspace
              </label>
              {workspaces.length > 1 ? (
                <select
                  className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 focus:border-lgb-primary focus:ring-lgb-primary"
                  id="oauth-workspace"
                  onChange={(event) => {
                    const workspace = workspaces.find(
                      (candidate) => candidate.workspaceId === event.target.value,
                    );
                    if (workspace) {
                      setErrorMessage(null);
                      setActiveWorkspace(workspace);
                    }
                  }}
                  value={activeWorkspace?.workspaceId ?? ""}
                >
                  {workspaces.map((workspace) => (
                    <option key={workspace.workspaceId} value={workspace.workspaceId}>
                      {workspace.workspaceName || workspace.workspaceId} ({workspace.role})
                    </option>
                  ))}
                </select>
              ) : (
                <p className="mt-1 text-sm font-medium text-gray-900">{workspaceName}</p>
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium uppercase text-gray-500">Access requested</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {requestedScopes.map((scope) => (
                <span
                  key={scope}
                  className="rounded-md border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-800"
                >
                  {scopeLabels[scope] || scope}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs leading-5 text-gray-500">
              {hasWriteAccess
                ? "This client can change data for the selected write scopes. You can revoke access at any time."
                : "This authorization creates read-only MCP access for the selected data."}
            </p>
            {hasWriteAccess && canApprove && !errorMessage && (
              <p className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-xs leading-5 text-yellow-900">
                Write access changes your workspace. Approve it only for a client you trust.
              </p>
            )}
          </div>

          {errorMessage && (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              {errorMessage}
            </p>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <Link
              className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              to="/settings"
            >
              Cancel
            </Link>
            <button
              className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-lgb-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-lgb-blue-350 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[180px]"
              disabled={!canApprove}
              onClick={approve}
              type="button"
            >
              {isApproving ? "Authorizing..." : "Authorize"}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OAuthAuthorizePage;
